// ...existing code...
import React, { useEffect, useMemo, useRef, useState } from "react";
import { providerApi, toApiAbsoluteUrl } from "../../api/client";

export default function ProviderProfile() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [gstNumber, setGstNumber] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [address, setAddress] = useState("");
  const [companyType, setCompanyType] = useState("");
  const [aboutCompany, setAboutCompany] = useState("");
  const [whyChooseUs, setWhyChooseUs] = useState("");
  const [files, setFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [gstError, setGstError] = useState("");
  const [panError, setPanError] = useState("");

  // Listing package state
  const [numberOfListings, setNumberOfListings] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [utrId, setUtrId] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [submissionDataRef, setSubmissionDataRef] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("pending");

  useEffect(() => {
    (async () => {
      try {
        const res = await providerApi.getMyProfile();
        const p = res?.providerProfile || null;
        setProfile(p);
        setGstNumber(p?.gstNumber || "");
        setPanNumber(p?.panNumber || "");
        setAddress(p?.address || "");
        setCompanyType(p?.companyType || "");
        setAboutCompany(p?.aboutCompany || "");
        setWhyChooseUs(p?.whyChooseUs || "");
        setNumberOfListings(p?.numberOfListings || "");
        setPaymentStatus(p?.paymentStatus || "pending");
      } catch (_err) {
        setError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isSet = (v) => v !== undefined && v !== null && String(v).trim() !== "";

  // Check if a package is actually selected (numberOfListings > 0)
  const hasPackageSelected = (num) => {
    const value = parseInt(num);
    return !isNaN(value) && value > 0;
  };

  // Calculate package amount based on number of listings
  const calculatePackageAmount = (count) => {
    if (!count) return 0;
    const num = parseInt(count);
    // 1 listing is free
    if (num === 1) return 0;
    // For 5+ listings, use the base pricing
    const baseAmount = 499;
    const multiplier = num / 5;
    return Math.round(baseAmount * multiplier);
  };

  const locked = useMemo(() => {
    const p = profile || {};
    return {
      companyName: isSet(p.companyName),
      phoneNumber: isSet(p.phoneNumber),
      gstNumber: isSet(p.gstNumber),
      panNumber: isSet(p.panNumber),
      address: isSet(p.address),
      companyType: isSet(p.companyType),
      aboutCompany: isSet(p.aboutCompany),
      whyChooseUs: isSet(p.whyChooseUs),
      documents: Array.isArray(p.documents) && p.documents.length > 0,
      numberOfListings: false, // Never lock package selection - allow upgrades
    };
  }, [profile]);

  function addFiles(list) {
    const incoming = Array.from(list || []);
    const allowed = ["image/jpeg", "image/png", "application/pdf"];
    const valid = incoming.filter(
      (f) => allowed.includes(f.type) && f.size <= 5 * 1024 * 1024
    );
    const remaining = Math.max(0, 5 - files.length);
    const toAdd = valid.slice(0, remaining);
    if (toAdd.length === 0) return;
    const nextFiles = [...files, ...toAdd];
    const nextPreviews = [...filePreviews];
    toAdd.forEach((f) => {
      if (f.type.startsWith("image/")) {
        try {
          nextPreviews.push(URL.createObjectURL(f));
        } catch {
          nextPreviews.push(null);
        }
      } else {
        nextPreviews.push(null);
      }
    });
    setFiles(nextFiles);
    setFilePreviews(nextPreviews);
  }

  function onFileChange(e) {
    addFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function removeFile(index) {
    const nextFiles = files.filter((_, i) => i !== index);
    const toRevoke = filePreviews[index];
    if (toRevoke) {
      try {
        URL.revokeObjectURL(toRevoke);
      } catch {}
    }
    const nextPreviews = filePreviews.filter((_, i) => i !== index);
    setFiles(nextFiles);
    setFilePreviews(nextPreviews);
  }

  function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return "";
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "0 B";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const val = bytes / Math.pow(1024, i);
    return `${val.toFixed(val >= 100 ? 0 : val >= 10 ? 1 : 2)} ${sizes[i]}`;
  }

  // --- new helpers for word limit ---
  function countWords(text) {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
  }

  function trimToMaxWords(text, max = 100) {
    if (!text) return "";
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length <= max) return text;
    return words.slice(0, max).join(" ");
  }

  function isValidGST(gst) {
    if (!gst) return false;
    const regex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return regex.test(gst.toUpperCase());
  }

  function isValidPAN(pan) {
    if (!pan) return false;
    const regex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    return regex.test(pan.toUpperCase());
  }

  // Payment modal functions
  const generateQRCode = (amount) => {
    const upiId = "9818877199-9@ybl";
    const upiLink = `upi://pay?pa=${upiId}&pn=OnlineIndianMarket&am=${amount}&cu=INR&tn=Listing+Package+Payment`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
      upiLink
    )}`;
  };

  const cancelVerification = () => {
    setShowPaymentModal(false);
    setUtrId("");
    setQrCodeUrl("");
    setSubmissionDataRef(null);
    setError("");
    setMessage("");
  };

  const confirmAndSubmit = async () => {
    if (!utrId || utrId.trim() === "") {
      setError("Please enter the UTR/transaction ID to continue.");
      return;
    }

    if (!submissionDataRef) {
      setError("Submission data unavailable. Please try again.");
      setShowPaymentModal(false);
      return;
    }

    setError("");
    setMessage("");
    setSubmitting(true);

    try {
      // Append UTR to FormData (package details are already included)
      submissionDataRef.append("utrId", utrId.trim());

      const res = await providerApi.updateMyProfile(submissionDataRef);
      const updatedProfile = res?.providerProfile || null;
      setProfile(updatedProfile);
      setGstNumber(updatedProfile?.gstNumber || "");
      setPanNumber(updatedProfile?.panNumber || "");
      setAddress(updatedProfile?.address || "");
      setCompanyType(updatedProfile?.companyType || "");
      setAboutCompany(updatedProfile?.aboutCompany || "");
      setWhyChooseUs(updatedProfile?.whyChooseUs || "");
      setNumberOfListings(updatedProfile?.numberOfListings || "");
      setPaymentStatus(updatedProfile?.paymentStatus || "pending");
      setFiles([]);

      const updated = Array.isArray(res?.updated) ? res.updated : [];
      const label = (k) =>
        ({
          gstNumber: "GST Number",
          panNumber: "PAN Number",
          address: "Address",
          companyType: "Company Type",
          documents: "Documents",
          aboutCompany: "About Company",
          whyChooseUs: "Why Choose Us",
          numberOfListings: "Listing Package",
        }[k] || k);
      const updatedMsg = updated.length
        ? `Updated ${updated.map(label).join(", ")}.`
        : "No changes were made.";
      setMessage(updatedMsg);

      setShowPaymentModal(false);
      setUtrId("");
      setQrCodeUrl("");
      setSubmissionDataRef(null);
    } catch (err) {
      const data = err?.data;
      const msg =
        data?.errors?.[0]?.message || data?.message || "Update failed";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  async function onSubmit(e) {
    e.preventDefault();
    setMessage("");
    setError("");
    setGstError("");
    setPanError("");
    // ✅ Prevent submit if GST number is invalid
    if (!locked.panNumber && isSet(panNumber) && !isValidPAN(panNumber)) {
      setPanError("Please enter a valid PAN number.");
      return; // stop submit
    }

    // ✅ Prevent submit if GST number is invalid
    if (!locked.gstNumber && isSet(gstNumber) && !isValidGST(gstNumber)) {
      setGstError("Please enter a valid GST number.");
      return; // stop submit
    }

    try {
      setSubmitting(true);
      const fd = new FormData();
      if (!locked.gstNumber && isSet(gstNumber))
        fd.append("gstNumber", gstNumber.trim());
      if (!locked.panNumber && isSet(panNumber))
        fd.append("panNumber", panNumber.trim());

      if (!locked.address && isSet(address))
        fd.append("address", address.trim());
      if (!locked.companyType && isSet(companyType))
        fd.append("companyType", companyType);
      if (!locked.aboutCompany && isSet(aboutCompany))
        fd.append("aboutCompany", aboutCompany.trim());
      if (!locked.whyChooseUs && isSet(whyChooseUs))
        fd.append("whyChooseUs", whyChooseUs.trim());

      if (!locked.documents && files.length > 0) {
        files.forEach((f) => fd.append("documents", f));
      }

      // Check if listing package is selected and it's a new selection or upgrade
      if (isSet(numberOfListings)) {
        // Use verifiedNumberOfListings for upgrade comparison (what's actually verified)
        // Fall back to numberOfListings if no verified package exists
        const verifiedListings = profile?.verifiedNumberOfListings || 0;
        const currentListings =
          verifiedListings > 0
            ? verifiedListings
            : profile?.numberOfListings || 0;
        const newListings = parseInt(numberOfListings);

        // Only show payment modal if it's a new package or an upgrade
        if (newListings > currentListings) {
          // Calculate upgrade amount (difference between new and current verified)
          const currentAmount = calculatePackageAmount(currentListings);
          const newAmount = calculatePackageAmount(numberOfListings);
          const upgradeAmount = newAmount - currentAmount;

          // Add package details to form data (store full amount for backend)
          fd.append("numberOfListings", numberOfListings);
          fd.append("packageAmount", newAmount.toString());
          // Store form data and show payment modal
          setSubmissionDataRef(fd);
          setQrCodeUrl(generateQRCode(upgradeAmount)); // Show only upgrade amount in QR
          setShowPaymentModal(true);
          setSubmitting(false);
          return; // Don't submit yet, wait for payment confirmation
        } else if (newListings === currentListings) {
          // Same package selected, no payment needed
          setMessage("Same package already selected. No changes needed.");
          setSubmitting(false);
          return;
        } else {
          // Downgrade not allowed
          setError("You can only upgrade to a higher package, not downgrade.");
          setSubmitting(false);
          return;
        }
      }

      // If no listing package or it's already locked, submit normally
      const res = await providerApi.updateMyProfile(fd);
      const updatedProfile = res?.providerProfile || null;
      setProfile(updatedProfile);
      setGstNumber(updatedProfile?.gstNumber || "");
      setPanNumber(updatedProfile?.panNumber || "");
      setAddress(updatedProfile?.address || "");
      setCompanyType(updatedProfile?.companyType || "");
      setAboutCompany(updatedProfile?.aboutCompany || "");
      setWhyChooseUs(updatedProfile?.whyChooseUs || "");
      setNumberOfListings(updatedProfile?.numberOfListings || "");
      setPaymentStatus(updatedProfile?.paymentStatus || "pending");
      setFiles([]);
      const updated = Array.isArray(res?.updated) ? res.updated : [];
      const label = (k) =>
        ({
          gstNumber: "GST Number",
          panNumber: "PAN Number",
          address: "Address",
          companyType: "Company Type",
          documents: "Documents",
          aboutCompany: "About Company",
          whyChooseUs: "Why Choose Us",
          numberOfListings: "Listing Package",
        }[k] || k);
      const updatedMsg = updated.length
        ? `Updated ${updated.map(label).join(", ")}.`
        : "No changes were made.";
      setMessage(updatedMsg);
    } catch (err) {
      const data = err?.data;
      const msg =
        data?.errors?.[0]?.message || data?.message || "Update failed";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading)
    return <div className="text-center text-gray-600">Loading profile...</div>;
  if (error) return <div className="text-center text-red-600">{error}</div>;
  if (!profile)
    return <div className="text-center text-gray-600">Profile not found</div>;

  return (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        Provider Profile
      </h2>

      {message && (
        <div className="mb-4 p-3 rounded bg-green-50 text-green-700 border border-green-200">
          {message}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              Company Name
            </label>
            <input
              type="text"
              value={profile.companyName || ""}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
            />
            <p className="text-xs text-gray-500 mt-1">
              Set during signup and cannot be changed.
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              Phone Number
            </label>
            <input
              type="text"
              value={profile.phoneNumber || ""}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
            />
            <p className="text-xs text-gray-500 mt-1">
              Set during signup and cannot be changed.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              GST Number
            </label>
            <input
              type="text"
              placeholder="Enter GST number"
              value={gstNumber}
              onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
              disabled={locked.gstNumber}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                locked.gstNumber
                  ? "bg-gray-100 text-gray-600 border-gray-300"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            />
            <p
              className={`text-xs mt-1 ${
                gstError
                  ? "text-red-500"
                  : gstNumber && !locked.gstNumber && !isValidGST(gstNumber)
                  ? "text-red-500"
                  : locked.gstNumber
                  ? "text-gray-500"
                  : "text-gray-400"
              }`}
            >
              {gstError
                ? gstError
                : gstNumber && !locked.gstNumber && !isValidGST(gstNumber)
                ? "Invalid GST number format."
                : locked.gstNumber
                ? "Already set and locked."
                : "You can set this once."}
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              PAN Number
            </label>
            <input
              type="text"
              placeholder="Enter PAN number"
              value={panNumber}
              onChange={(e) => setPanNumber(e.target.value.toUpperCase())}
              disabled={locked.panNumber}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                locked.panNumber
                  ? "bg-gray-100 text-gray-600 border-gray-300"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            />
            <p
              className={`text-xs mt-1 ${
                panError
                  ? "text-red-500"
                  : panNumber && !locked.panNumber && !isValidPAN(panNumber)
                  ? "text-red-500"
                  : locked.panNumber
                  ? "text-gray-500"
                  : "text-gray-400"
              }`}
            >
              {panError
                ? panError
                : panNumber && !locked.panNumber && !isValidPAN(panNumber)
                ? "Invalid PAN number format."
                : locked.panNumber
                ? "Already set and locked."
                : "You can set this once."}
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              Address
            </label>
            <input
              type="text"
              placeholder="Enter address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={locked.address}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                locked.address
                  ? "bg-gray-100 text-gray-600 border-gray-300"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            />
            <p
              className={`text-xs mt-1 ${
                locked.address ? "text-gray-500" : "text-gray-400"
              }`}
            >
              {locked.address
                ? "Already set and locked."
                : "You can set this once."}
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
              Company Type
            </label>
            <select
              value={companyType || ""}
              onChange={(e) => setCompanyType(e.target.value)}
              disabled={locked.companyType}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                locked.companyType
                  ? "bg-gray-100 text-gray-600 border-gray-300"
                  : "border-gray-300 focus:ring-blue-400"
              }`}
            >
              <option value="">Select company type</option>
              <option value="manufacturer">Manufacturer</option>
              <option value="wholesaler">Wholesaler</option>
              <option value="distributor">Distributor</option>
              <option value="supplier">Supplier</option>
              <option value="industrial_services">Industrial Services</option>
              <option value="exporters">Exporters</option>
              <option value="traders">Traders</option>
              <option value="other">Other</option>
            </select>
            <p
              className={`text-xs mt-1 ${
                locked.companyType ? "text-gray-500" : "text-gray-400"
              }`}
            >
              {locked.companyType
                ? "Already set and locked."
                : "You can set this once."}
            </p>
          </div>
        </div>

        {/* Listing Package */}
        <div>
          <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
            Listing Package
          </label>
          <select
            value={numberOfListings || ""}
            onChange={(e) => setNumberOfListings(e.target.value)}
            disabled={
              hasPackageSelected(profile?.numberOfListings) &&
              paymentStatus === "pending"
            }
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
              hasPackageSelected(profile?.numberOfListings) &&
              paymentStatus === "pending"
                ? "bg-gray-100 text-gray-600 border-gray-300"
                : "border-gray-300 focus:ring-blue-400"
            }`}
          >
            <option value="">Select number of listings</option>
            <option value="1">1 Listing - Free</option>
            <option value="5">5 Listings - ₹499</option>
            <option value="10">10 Listings - ₹998</option>
            <option value="15">15 Listings - ₹1,497</option>
            <option value="20">20 Listings - ₹1,996</option>
            <option value="25">25 Listings - ₹2,495</option>
          </select>
          <div className="flex justify-between text-xs mt-1">
            <div
              className={
                hasPackageSelected(profile?.numberOfListings) &&
                paymentStatus === "pending"
                  ? "text-yellow-600"
                  : paymentStatus === "rejected"
                  ? "text-red-600"
                  : paymentStatus === "verified"
                  ? "text-green-600"
                  : "text-gray-400"
              }
            >
              {hasPackageSelected(profile?.numberOfListings) &&
              paymentStatus === "pending"
                ? "Payment pending verification by admin."
                : paymentStatus === "rejected"
                ? "Payment rejected. You can select a new package."
                : paymentStatus === "verified"
                ? `Package verified! You can upgrade to a higher package.`
                : "Select your listing package."}
            </div>
            {numberOfListings &&
              !(
                hasPackageSelected(profile?.numberOfListings) &&
                paymentStatus === "pending"
              ) && (
                <div className="text-blue-600 font-medium">
                  {hasPackageSelected(profile?.numberOfListings) &&
                  parseInt(numberOfListings) > (profile?.numberOfListings || 0)
                    ? (() => {
                        const currentAmount = calculatePackageAmount(
                          profile?.numberOfListings || 0
                        );
                        const newAmount =
                          calculatePackageAmount(numberOfListings);
                        const upgradeAmount = newAmount - currentAmount;
                        return `Upgrade Amount: ₹${upgradeAmount.toLocaleString()}`;
                      })()
                    : `Amount: ₹${calculatePackageAmount(
                        numberOfListings
                      ).toLocaleString()}`}
                </div>
              )}
          </div>

          {/* Payment Status Display */}
          {hasPackageSelected(profile?.numberOfListings) &&
            paymentStatus === "pending" && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-yellow-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-800">
                      Your payment is pending verification.{" "}
                      {(() => {
                        const verifiedCount =
                          profile?.verifiedNumberOfListings || 0;
                        const hasVerifiedPackage =
                          verifiedCount > 0 || profile?.paymentVerifiedAt;

                        if (hasVerifiedPackage) {
                          return (
                            <>
                              You currently have{" "}
                              <strong>{verifiedCount} listings</strong>{" "}
                              available. Once admin verifies your payment,
                              you'll be able to create{" "}
                              <strong>
                                {profile?.numberOfListings || numberOfListings}{" "}
                                listings
                              </strong>
                              .
                            </>
                          );
                        } else {
                          return (
                            <>
                              You currently have <strong>0 listings</strong>{" "}
                              available. Once admin verifies your payment,
                              you'll be able to create{" "}
                              <strong>
                                {profile?.numberOfListings || numberOfListings}{" "}
                                listings
                              </strong>
                              .
                            </>
                          );
                        }
                      })()}
                    </p>
                  </div>
                </div>
              </div>
            )}

          {paymentStatus === "rejected" &&
            hasPackageSelected(profile?.numberOfListings) && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">
                      Your payment was rejected. Please select a new package and
                      try again.
                    </p>
                  </div>
                </div>
              </div>
            )}

          {paymentStatus === "verified" &&
            hasPackageSelected(profile?.numberOfListings) && (
              <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-green-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-800">
                      Payment verified! You can now create up to{" "}
                      <strong>{profile?.numberOfListings} listings</strong>.
                    </p>
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* New one-time paragraph fields */}
        <div>
          <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
            About Company
          </label>
          <textarea
            value={aboutCompany}
            onChange={(e) =>
              setAboutCompany(trimToMaxWords(e.target.value, 100))
            }
            disabled={locked.aboutCompany}
            rows={5}
            maxLength={2000}
            placeholder="Write a short description about your company (max 100 words). Can be set once."
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
              locked.aboutCompany
                ? "bg-gray-100 text-gray-600 border-gray-300"
                : "border-gray-300 focus:ring-blue-400"
            }`}
          />
          <div className="flex justify-between text-xs mt-1">
            <div
              className={
                locked.aboutCompany ? "text-gray-500" : "text-gray-400"
              }
            >
              {locked.aboutCompany
                ? "Already set and locked."
                : "You can set this once."}
            </div>
            <div
              className={`text-gray-400 ${
                countWords(aboutCompany) >= 100 ? "text-red-500" : ""
              }`}
            >
              {countWords(aboutCompany)}/100 words
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
            Why Choose Us
          </label>
          <textarea
            value={whyChooseUs}
            onChange={(e) =>
              setWhyChooseUs(trimToMaxWords(e.target.value, 100))
            }
            disabled={locked.whyChooseUs}
            rows={5}
            maxLength={2000}
            placeholder="Explain why customers should choose your company (max 100 words). Can be set once."
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
              locked.whyChooseUs
                ? "bg-gray-100 text-gray-600 border-gray-300"
                : "border-gray-300 focus:ring-blue-400"
            }`}
          />
          <div className="flex justify-between text-xs mt-1">
            <div
              className={locked.whyChooseUs ? "text-gray-500" : "text-gray-400"}
            >
              {locked.whyChooseUs
                ? "Already set and locked."
                : "You can set this once."}
            </div>
            <div
              className={`text-gray-400 ${
                countWords(whyChooseUs) >= 100 ? "text-red-500" : ""
              }`}
            >
              {countWords(whyChooseUs)}/100 words
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold uppercase text-gray-600 mb-1">
            Documents
          </label>
          {locked.documents ? (
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 text-gray-600">
              Documents already uploaded and locked.
            </div>
          ) : (
            <>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  addFiles(e.dataTransfer.files);
                }}
                onClick={() => fileInputRef.current?.click()}
                className={`mt-1 w-full flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed cursor-pointer ${
                  isDragging
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                }`}
              >
                <div className="text-sm text-gray-700">
                  Drag & drop files here, or click to browse
                </div>
                <div className="text-xs text-gray-500">
                  Up to 5 files (JPG/PNG/PDF), max 5MB each. You can upload
                  once.
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/png,image/jpeg,application/pdf"
                  className="hidden"
                  onChange={onFileChange}
                />
              </div>
              <div className="mt-3 space-y-2">
                {files.length === 0 && (
                  <div className="text-xs text-gray-500">No files selected</div>
                )}
                {files.map((file, i) => (
                  <div
                    key={`${file.name}-${i}`}
                    className="flex items-center gap-3 p-2 border rounded bg-white"
                  >
                    <div className="h-12 w-12 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                      {file.type.startsWith("image/") && filePreviews[i] ? (
                        <img
                          src={filePreviews[i]}
                          alt={file.name}
                          className="object-cover h-full w-full"
                        />
                      ) : (
                        <span className="text-[10px] text-gray-500">
                          {file.type.includes("pdf") ? "PDF" : "FILE"}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-sm text-gray-800 truncate"
                        title={file.name}
                      >
                        {file.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatBytes(file.size)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="text-right">
          <button
            type="submit"
            disabled={submitting}
            className={`bg-gradient-to-r from-blue-500 to-blue-700 uppercase px-6 py-2 text-white rounded-lg mulish-bold transition ${
              submitting
                ? "opacity-50 cursor-not-allowed"
                : "hover:text-blue-200"
            }`}
          >
            {submitting ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>

      {/* Existing documents preview */}
      <div className="mt-10">
        <h3 className="text-xl font-semibold text-gray-800 mb-3">
          Uploaded Documents
        </h3>
        {Array.isArray(profile.documents) && profile.documents.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {profile.documents.map((doc) => {
              const isImage = (doc?.mimeType || "").startsWith("image/");
              const isPdf = (doc?.mimeType || "").includes("pdf");
              const absUrl = toApiAbsoluteUrl(doc?.url);
              return (
                <div
                  key={doc.id || doc._id || doc.url}
                  className="group bg-white rounded-2xl shadow-md ring-1 ring-gray-100 overflow-hidden hover:shadow-lg transition"
                >
                  <div className="relative">
                    {isImage ? (
                      <img
                        src={absUrl}
                        alt={doc?.originalName || "document"}
                        className="h-40 w-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = `https://placehold.co/600x300/png?text=${encodeURIComponent(
                            doc?.originalName || "Document"
                          )}`;
                        }}
                      />
                    ) : (
                      <div className="h-40 w-full flex items-center justify-center bg-gray-50 text-gray-500">
                        <span className="text-sm font-medium">
                          {isPdf ? "PDF Document" : doc?.mimeType || "File"}
                        </span>
                      </div>
                    )}
                    <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                      {formatBytes(doc?.size)}
                    </div>
                  </div>
                  <div className="p-3">
                    <div
                      className="text-sm font-medium text-gray-900 truncate"
                      title={doc?.originalName}
                    >
                      {doc?.originalName || "Document"}
                    </div>
                    <div
                      className="text-xs text-gray-500 mt-1 truncate"
                      title={doc?.mimeType}
                    >
                      {doc?.mimeType}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <a
                        href={absUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        View
                      </a>
                      {doc?.uploadedAt && (
                        <span className="text-[10px] text-gray-400">
                          {new Date(doc.uploadedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-gray-500">No documents uploaded yet.</div>
        )}
      </div>

      {/* Payment Verification Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Payment Verification
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Please scan the QR code and pay ₹
                {(() => {
                  const currentListings = profile?.numberOfListings || 0;
                  const newListings = parseInt(numberOfListings);
                  if (newListings > currentListings) {
                    const currentAmount =
                      calculatePackageAmount(currentListings);
                    const newAmount = calculatePackageAmount(numberOfListings);
                    const upgradeAmount = newAmount - currentAmount;
                    return upgradeAmount.toLocaleString();
                  }
                  return calculatePackageAmount(
                    numberOfListings
                  ).toLocaleString();
                })()}{" "}
                for {numberOfListings} listings package
                {hasPackageSelected(profile?.numberOfListings) &&
                  parseInt(numberOfListings) >
                    (profile?.numberOfListings || 0) && (
                    <span className="block mt-1 text-blue-600 font-medium">
                      (Upgrading from {profile?.numberOfListings || 0} listings)
                    </span>
                  )}
              </p>

              {/* QR Code */}
              <div className="flex justify-center mb-4">
                <img
                  src={qrCodeUrl}
                  alt="Payment QR Code"
                  className="w-48 h-48 border rounded"
                />
              </div>

              {/* UTR Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter UTR/Transaction ID
                </label>
                <input
                  type="text"
                  value={utrId}
                  onChange={(e) => setUtrId(e.target.value)}
                  placeholder="Enter UTR ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={cancelVerification}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAndSubmit}
                  disabled={submitting}
                  className={`flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors ${
                    submitting ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {submitting ? "Processing..." : "Confirm & Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
